/**
 * WhatsNewCalloutsContext — the three strictly-rationed What's New callout
 * levels (RW-1642), computed server-side (changelogApi `callouts`) and applied
 * here:
 *
 *   1. Count pill  — badges.whatsNewUnread on the nav item. Cleared by opening
 *      /whats-new (the page calls markVisited then refresh()).
 *   2. NEW chip    — only for `new`-tagged entries, only on the nav item the
 *      feature lives under. Cleared the first time the agent USES the feature —
 *      v1 heuristic: first navigation to the feature's route (ackModule).
 *   3. Spotlight   — `featured` entries only, shown ONCE, on first visit to the
 *      screen that changed. Never stacks: if two qualify the older yields.
 *      Dismiss is permanent (ackSpotlight writes the per-user seen map).
 *
 * All state lives on users/{uid}.whatsNew* and is written ONLY by the callable
 * (Admin SDK) — no client Firestore writes, no rules changes. The provider
 * refetches on the changelog signal doc, so a publish moves the pill live.
 * New-hire guard (server-side): nothing published before the account existed
 * generates callouts (Auth creation time, 45-day window cap).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { changelogService } from '../services/changelog';
import { useChangelogSignalRev } from '../hooks/useChangelog';
import { MODULE_META } from '../components/whatsnew/wnKit';

const WhatsNewCalloutsContext = createContext(null);

export function useWhatsNewCallouts() {
  return useContext(WhatsNewCalloutsContext);
}

/** Thin provider — App owns the state via useWhatsNewCalloutsState so it can
 *  also feed the Sidebar badge/chips without a consumer indirection. */
export function WhatsNewCalloutsProvider({ value, children }) {
  return (
    <WhatsNewCalloutsContext.Provider value={value}>
      {children}
    </WhatsNewCalloutsContext.Provider>
  );
}

function moduleForPath(pathname) {
  let best = null;
  for (const [module, meta] of Object.entries(MODULE_META)) {
    if (pathname === meta.route || pathname.startsWith(meta.route + '/')) {
      if (!best || meta.route.length > MODULE_META[best].route.length) best = module;
    }
  }
  return best;
}

export function useWhatsNewCalloutsState(user, enabled = true) {
  const location = useLocation();
  const rev = useChangelogSignalRev();
  const [callouts, setCallouts] = useState(null);
  const [activeSpotlight, setActiveSpotlight] = useState(null);
  const uid = user?.uid || null;
  const active = enabled && !!uid;

  const refresh = useCallback(async () => {
    if (!active) return;
    try {
      setCallouts(await changelogService.callouts());
    } catch (_) { /* quiet — callouts are decoration, never block the app */ }
  }, [active]);

  useEffect(() => { refresh(); }, [refresh, rev]);
  useEffect(() => { if (!active) { setCallouts(null); setActiveSpotlight(null); } }, [active]);

  // Level 2 — route-visit heuristic: navigating into a module with an active
  // NEW chip counts as using the feature; the chip clears permanently.
  const ackedRef = useRef(new Set());
  useEffect(() => {
    if (!active || !callouts?.chips?.length) return;
    const module = moduleForPath(location.pathname);
    if (!module || ackedRef.current.has(module)) return;
    if (!callouts.chips.some((c) => c.module === module)) return;
    ackedRef.current.add(module);
    setCallouts((c) => c ? { ...c, chips: c.chips.filter((ch) => ch.module !== module) } : c);
    changelogService.ackModule(module).catch(() => { ackedRef.current.delete(module); });
  }, [active, callouts, location.pathname]);

  // Level 3 — one spotlight, first visit to the changed screen, newest wins.
  useEffect(() => {
    if (!active || activeSpotlight || !callouts?.spotlights?.length) return;
    if (location.pathname.startsWith('/whats-new')) return; // the log speaks for itself
    const module = moduleForPath(location.pathname);
    if (!module) return;
    const qualifying = callouts.spotlights
      .filter((s) => s.module === module)
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
    if (!qualifying.length) return;
    const [show, ...yielded] = qualifying;
    setActiveSpotlight(show);
    setCallouts((c) => c ? { ...c, spotlights: c.spotlights.filter((s) => s.module !== module) } : c);
    // The older ones yield permanently — they never stack or reappear.
    changelogService.ackSpotlight(show.id, yielded.map((y) => y.id)).catch(() => {});
  }, [active, activeSpotlight, callouts, location.pathname]);

  const dismissSpotlight = useCallback(() => setActiveSpotlight(null), []);

  // Sidebar item keys that should carry the NEW chip.
  const highlightKeys = useMemo(() => {
    const keys = new Set();
    for (const c of callouts?.chips || []) {
      const key = MODULE_META[c.module]?.chipKey;
      if (key) keys.add(key);
    }
    return keys;
  }, [callouts]);

  return useMemo(() => ({
    unreadCount: callouts?.unreadCount || 0,
    chips: callouts?.chips || [],
    highlightKeys,
    activeSpotlight,
    dismissSpotlight,
    refresh,
  }), [callouts, highlightKeys, activeSpotlight, dismissSpotlight, refresh]);
}
